import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  ArrowRight, Leaf, CurrencyDollar, Plus, Robot, Spinner,
  X, CheckCircle, Target, Lightning, Factory
} from '@phosphor-icons/react';
import { oeeCustomScenario } from '../api';

const CATEGORY_COLORS = {
  'Tariff Optimization': '#4ade80',
  'Utility Efficiency': '#60a5fa',
  'Energy Storage': '#facc15',
  Maintenance: '#94a3b8',
  Quality: '#f97316',
  Combined: '#c084fc',
  Custom: '#e879f9',
};

const OBJECTIVE_OPTIONS = [
  { key: 'esg', label: 'ESG / Carbon Reduction', icon: Leaf, color: 'text-teal-400', bg: 'bg-teal-500/10' },
  { key: 'opex', label: 'Operational Efficiency', icon: Factory, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { key: 'cost', label: 'Cost Savings', icon: CurrencyDollar, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

const PARAMETER_OPTIONS = [
  { key: 'shift_to_offpeak', label: 'Shift production to off-peak hours' },
  { key: 'reduce_leakage', label: 'Reduce compressed air / steam leakage' },
  { key: 'add_vfd', label: 'Install VFD on motors' },
  { key: 'predictive_maintenance', label: 'Deploy predictive maintenance' },
  { key: 'solar_battery', label: 'Add solar PV / battery storage' },
  { key: 'recipe_control', label: 'Tighten recipe / process control' },
  { key: 'water_recycling', label: 'Implement water recycling' },
  { key: 'smed_changeover', label: 'SMED / green changeover' },
  { key: 'heat_recovery', label: 'Heat recovery from ovens / compressors' },
  { key: 'worker_safety', label: 'Worker heat-stress / IAQ monitoring' },
  { key: 'supplier_quality', label: 'Supplier ESG-quality scorecards' },
  { key: 'custom', label: 'Custom parameter (describe below)' },
];

function fmtVnd(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function ScenarioBuilder({ lines, onClose, onResult }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetLines, setTargetLines] = useState([]);
  const [selectedParams, setSelectedParams] = useState([]);
  const [customParam, setCustomParam] = useState('');
  const [objectives, setObjectives] = useState({ esg: false, opex: false, cost: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleLine = (lid) => {
    setTargetLines(prev => prev.includes(lid) ? prev.filter(l => l !== lid) : [...prev, lid]);
  };
  const toggleParam = (key) => {
    setSelectedParams(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };
  const toggleObj = (key) => {
    setObjectives(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const canSubmit = title.trim() && description.trim() && selectedParams.length > 0 && Object.values(objectives).some(Boolean);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const paramChanges = {};
      selectedParams.forEach(p => {
        const opt = PARAMETER_OPTIONS.find(o => o.key === p);
        paramChanges[p] = opt?.label || p;
      });
      if (customParam.trim()) paramChanges.custom_detail = customParam;

      const activeObjectives = {};
      Object.entries(objectives).forEach(([k, v]) => {
        if (v) activeObjectives[k] = OBJECTIVE_OPTIONS.find(o => o.key === k)?.label || k;
      });

      const res = await oeeCustomScenario({
        title,
        description,
        target_lines: targetLines,
        parameter_changes: paramChanges,
        objectives: activeObjectives,
      });
      onResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Scenario simulation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-surface border border-primary/30 rounded-lg p-6 animate-fade-in" data-testid="oee-scenario-builder">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Robot size={20} className="text-violet-400" weight="fill" />
          <h3 className="font-heading text-base font-semibold text-text-primary">AI Scenario Builder</h3>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-text-secondary hover:text-text-primary transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="space-y-5">
        {/* Title & Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider block mb-1.5">Scenario Title *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Night Shift Optimization"
              data-testid="oee-builder-title"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-body text-text-primary placeholder:text-text-secondary/50 focus:ring-1 focus:ring-primary focus:border-primary" />
          </div>
          <div>
            <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider block mb-1.5">Description *</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what you want to simulate..."
              data-testid="oee-builder-desc"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-body text-text-primary placeholder:text-text-secondary/50 focus:ring-1 focus:ring-primary focus:border-primary" />
          </div>
        </div>

        {/* Objectives */}
        <div>
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider block mb-2">Objectives *</label>
          <div className="flex flex-wrap gap-2">
            {OBJECTIVE_OPTIONS.map(obj => {
              const Icon = obj.icon;
              const active = objectives[obj.key];
              return (
                <button key={obj.key} onClick={() => toggleObj(obj.key)}
                  data-testid={`oee-builder-obj-${obj.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-body font-medium border transition-all ${
                    active ? `${obj.bg} ${obj.color} border-current` : 'border-border text-text-secondary hover:border-tertiary/40'
                  }`}>
                  <Icon size={14} weight={active ? 'fill' : 'regular'} />
                  {obj.label}
                  {active && <CheckCircle size={12} weight="fill" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Target Lines */}
        <div>
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider block mb-2">Target Lines <span className="text-text-secondary/60">(optional — leave empty for all)</span></label>
          <div className="flex flex-wrap gap-2">
            {(lines || []).map(l => {
              const active = targetLines.includes(l.line_id);
              return (
                <button key={l.line_id} onClick={() => toggleLine(l.line_id)}
                  data-testid={`oee-builder-line-${l.line_id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium border transition-all ${
                    active ? 'bg-primary/15 text-primary border-primary/50' : 'border-border text-text-secondary hover:border-tertiary/40'
                  }`}>
                  {l.line_id}: {l.name?.split('—')[1]?.trim() || l.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Parameter Changes */}
        <div>
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider block mb-2">Parameter Changes *</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {PARAMETER_OPTIONS.map(p => {
              const active = selectedParams.includes(p.key);
              return (
                <button key={p.key} onClick={() => toggleParam(p.key)}
                  data-testid={`oee-builder-param-${p.key}`}
                  className={`text-left px-3 py-2 rounded-lg text-xs font-body border transition-all ${
                    active ? 'bg-primary/10 text-primary border-primary/40 font-medium' : 'border-border text-text-secondary hover:border-tertiary/40'
                  }`}>
                  {active ? '+ ' : ''}{p.label}
                </button>
              );
            })}
          </div>
          {selectedParams.includes('custom') && (
            <textarea value={customParam} onChange={e => setCustomParam(e.target.value)}
              placeholder="Describe your custom parameter change..."
              className="mt-2 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-body text-text-primary placeholder:text-text-secondary/50 focus:ring-1 focus:ring-primary h-20 resize-none" />
          )}
        </div>

        {/* Submit */}
        {error && <p className="text-xs font-body text-red-400">{error}</p>}
        <button onClick={handleSubmit} disabled={!canSubmit || loading}
          data-testid="oee-builder-submit"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-white font-body font-semibold text-sm hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          {loading ? <><Spinner size={16} className="animate-spin" /> Simulating with Claude Sonnet 4.5...</> : <><Robot size={16} weight="fill" /> Run AI Scenario Simulation</>}
        </button>
      </div>
    </div>
  );
}

function CustomScenarioResult({ result, onClose }) {
  const p = result.projections || {};
  const sc = result.scenario || {};
  const hasProjections = p.projected_before && p.projected_after && p.delta;

  return (
    <div className="bg-surface border border-violet-500/30 rounded-lg p-6 animate-fade-in" data-testid="oee-custom-result">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-body font-medium px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">Custom — AI Generated</span>
            {p.confidence && <span className="text-[10px] font-body text-text-secondary">Confidence: {p.confidence}</span>}
          </div>
          <h3 className="font-heading text-base font-semibold text-text-primary">{sc.title}</h3>
          <p className="text-xs font-body text-text-secondary mt-0.5">{sc.description}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-text-secondary">
          <X size={16} />
        </button>
      </div>

      {hasProjections ? (
        <>
          {/* Before / After */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bg-background rounded-lg p-4">
              <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-3">Before</p>
              <div className="space-y-2 text-sm font-body">
                <div className="flex justify-between"><span className="text-text-secondary">OEE</span><span className="text-text-primary font-medium">{p.projected_before.oee}%</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Energy Cost</span><span className="text-text-primary font-medium">{fmtVnd(p.projected_before.energy_cost_vnd)} VND</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">CO2e</span><span className="text-text-primary font-medium">{p.projected_before.co2e_kg} kg</span></div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-1">
                <ArrowRight size={24} className="text-violet-400" weight="bold" />
                <span className="text-[10px] font-body text-text-secondary uppercase tracking-wider">AI Projected</span>
              </div>
            </div>
            <div className="bg-background rounded-lg p-4 border border-emerald-500/20">
              <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-emerald-400 mb-3">After</p>
              <div className="space-y-2 text-sm font-body">
                <div className="flex justify-between"><span className="text-text-secondary">OEE</span><span className="text-emerald-400 font-medium">{p.projected_after.oee}%</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">Energy Cost</span><span className="text-emerald-400 font-medium">{fmtVnd(p.projected_after.energy_cost_vnd)} VND</span></div>
                <div className="flex justify-between"><span className="text-text-secondary">CO2e</span><span className="text-emerald-400 font-medium">{p.projected_after.co2e_kg} kg</span></div>
              </div>
            </div>
          </div>
          {/* Delta strip */}
          <div className="flex flex-wrap items-center justify-center gap-6 bg-background rounded-lg p-4 mb-4">
            <div className="text-center">
              <p className="font-heading text-lg font-bold text-emerald-400">+{p.delta.oee_points} pp</p>
              <p className="text-[10px] font-body text-text-secondary">OEE Improvement</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg font-bold text-amber-400">{fmtVnd(p.delta.vnd_saved)} VND</p>
              <p className="text-[10px] font-body text-text-secondary">Cost Saved/Day</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg font-bold text-teal-400">-{p.delta.co2e_reduced_kg} kg</p>
              <p className="text-[10px] font-body text-text-secondary">CO2e Reduced/Day</p>
            </div>
            {p.payback_days && (
              <div className="text-center">
                <p className="font-heading text-lg font-bold text-blue-400">{p.payback_days}d</p>
                <p className="text-[10px] font-body text-text-secondary">Payback Period</p>
              </div>
            )}
            {p.esg_rating && (
              <div className="text-center">
                <p className="font-heading text-lg font-bold text-violet-400">{p.esg_rating}</p>
                <p className="text-[10px] font-body text-text-secondary">ESG Rating</p>
              </div>
            )}
          </div>
          {/* Plan & Risks */}
          {(p.implementation_plan || p.risks) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {p.implementation_plan && (
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-1">Implementation Plan</p>
                  <p className="text-xs font-body text-text-primary">{p.implementation_plan}</p>
                </div>
              )}
              {p.risks && (
                <div className="bg-background rounded-lg p-3">
                  <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-1">Key Risks</p>
                  <p className="text-xs font-body text-text-primary">{p.risks}</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-background rounded-lg p-4">
          <p className="text-sm font-body text-text-secondary whitespace-pre-wrap">{p.raw_analysis || JSON.stringify(p, null, 2)}</p>
        </div>
      )}
    </div>
  );
}

export default function OeeScenarios({ data }) {
  const scenarios = data?.scenarios || [];
  const customScenarios = data?.custom_scenarios || [];
  const lines = data?.lines || [];
  const [selected, setSelected] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [customResult, setCustomResult] = useState(null);
  const [viewTab, setViewTab] = useState('preset');

  const allCustom = [...customScenarios, ...(customResult ? [customResult.scenario] : [])];

  const compData = scenarios.map(s => ({
    name: s.scenario_id,
    oee_gain: s.delta.oee_points,
    vnd_saved: s.delta.vnd_saved,
    co2_reduced: s.delta.co2e_reduced_kg,
    title: s.title,
    category: s.category,
  }));

  const active = selected !== null ? scenarios[selected] : null;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-scenarios">
      {/* Header with toggle and builder button */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-surface border border-border rounded-lg p-1">
          <button onClick={() => setViewTab('preset')} data-testid="oee-scenario-tab-preset"
            className={`px-4 py-1.5 rounded-md text-xs font-body font-medium transition-all ${viewTab === 'preset' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}>
            Pre-Built ({scenarios.length})
          </button>
          <button onClick={() => setViewTab('custom')} data-testid="oee-scenario-tab-custom"
            className={`px-4 py-1.5 rounded-md text-xs font-body font-medium transition-all ${viewTab === 'custom' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}>
            Custom ({allCustom.length})
          </button>
        </div>
        <button onClick={() => { setShowBuilder(true); setViewTab('custom'); }}
          data-testid="oee-create-scenario-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 transition-all text-xs font-body font-semibold">
          <Plus size={14} weight="bold" /> Create Custom Scenario
        </button>
      </div>

      {viewTab === 'preset' && (
        <>
          {/* Comparison Chart */}
          <div className="bg-surface border border-border rounded-lg p-5">
            <h2 className="font-heading text-base font-semibold text-text-primary mb-4">Scenario Impact Comparison</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compData} margin={{ left: 0, right: 20 }}>
                  <XAxis dataKey="name" tick={{ fill: '#A3A380', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#A3A380', fontSize: 11 }} />
                  <Tooltip content={({ active: a, payload }) => {
                    if (!a || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
                        <p className="font-semibold text-text-primary mb-1">{d?.title}</p>
                        <p className="text-emerald-400">OEE: +{d?.oee_gain} pp</p>
                        <p className="text-amber-400">VND: {fmtVnd(d?.vnd_saved)}</p>
                        <p className="text-teal-400">CO2e: -{d?.co2_reduced} kg</p>
                      </div>
                    );
                  }} />
                  <Legend />
                  <Bar dataKey="oee_gain" name="OEE Gain (pp)" fill="#4ade80" radius={[4, 4, 0, 0]} barSize={20} />
                  <Bar dataKey="co2_reduced" name="CO2e Reduced (kg)" fill="#38bdf8" radius={[4, 4, 0, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Scenario Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map((sc, idx) => {
              const isActive = selected === idx;
              const catColor = CATEGORY_COLORS[sc.category] || '#6b7280';
              return (
                <button key={sc.scenario_id} onClick={() => setSelected(isActive ? null : idx)}
                  data-testid={`oee-scenario-${sc.scenario_id}`}
                  className={`text-left bg-surface border rounded-lg p-4 transition-all duration-200 hover:border-tertiary/40 ${isActive ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-body font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: catColor + '20', color: catColor }}>{sc.category}</span>
                    <span className="text-xs font-body font-bold text-emerald-400">+{sc.delta.oee_points} pp</span>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-text-primary mb-1">{sc.title}</h3>
                  <p className="text-xs font-body text-text-secondary mb-3 line-clamp-2">{sc.description}</p>
                  <div className="flex gap-3 text-[10px] font-body">
                    <span className="flex items-center gap-1 text-amber-400"><CurrencyDollar size={10} /> {fmtVnd(sc.delta.vnd_saved)}</span>
                    <span className="flex items-center gap-1 text-teal-400"><Leaf size={10} /> -{sc.delta.co2e_reduced_kg}kg</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Scenario Detail */}
          {active && (
            <div className="bg-surface border border-primary/30 rounded-lg p-6 animate-fade-in" data-testid="oee-scenario-detail">
              <h3 className="font-heading text-base font-semibold text-text-primary mb-1">{active.title}</h3>
              <p className="text-sm font-body text-text-secondary mb-5">{active.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="bg-background rounded-lg p-4">
                  <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-text-secondary mb-3">Before</p>
                  <div className="space-y-2 text-sm font-body">
                    <div className="flex justify-between"><span className="text-text-secondary">OEE</span><span className="text-text-primary font-medium">{active.before.oee}%</span></div>
                    <div className="flex justify-between"><span className="text-text-secondary">Energy Cost</span><span className="text-text-primary font-medium">{fmtVnd(active.before.energy_cost_vnd)} VND</span></div>
                    <div className="flex justify-between"><span className="text-text-secondary">CO2e</span><span className="text-text-primary font-medium">{active.before.co2e_kg} kg</span></div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowRight size={24} className="text-primary" weight="bold" />
                </div>
                <div className="bg-background rounded-lg p-4 border border-emerald-500/20">
                  <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-emerald-400 mb-3">After</p>
                  <div className="space-y-2 text-sm font-body">
                    <div className="flex justify-between"><span className="text-text-secondary">OEE</span><span className="text-emerald-400 font-medium">{active.after.oee}%</span></div>
                    <div className="flex justify-between"><span className="text-text-secondary">Energy Cost</span><span className="text-emerald-400 font-medium">{fmtVnd(active.after.energy_cost_vnd)} VND</span></div>
                    <div className="flex justify-between"><span className="text-text-secondary">CO2e</span><span className="text-emerald-400 font-medium">{active.after.co2e_kg} kg</span></div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 bg-background rounded-lg p-4">
                <div className="text-center">
                  <p className="font-heading text-lg font-bold text-emerald-400">+{active.delta.oee_points} pp</p>
                  <p className="text-[10px] font-body text-text-secondary">OEE Improvement</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-lg font-bold text-amber-400">{fmtVnd(active.delta.vnd_saved)} VND</p>
                  <p className="text-[10px] font-body text-text-secondary">Cost Saved</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-lg font-bold text-teal-400">-{active.delta.co2e_reduced_kg} kg</p>
                  <p className="text-[10px] font-body text-text-secondary">CO2e Reduced</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {viewTab === 'custom' && (
        <>
          {/* Builder Form */}
          {showBuilder && (
            <ScenarioBuilder
              lines={lines}
              onClose={() => setShowBuilder(false)}
              onResult={(res) => { setCustomResult(res); setShowBuilder(false); }}
            />
          )}

          {/* Custom Result */}
          {customResult && (
            <CustomScenarioResult result={customResult} onClose={() => setCustomResult(null)} />
          )}

          {/* Saved custom scenarios */}
          {allCustom.length > 0 && !customResult && (
            <div className="space-y-3">
              <h3 className="font-heading text-sm font-semibold text-text-primary">Saved Custom Scenarios</h3>
              {allCustom.map((sc, idx) => (
                <div key={idx} className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-body font-medium px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400">{sc.scenario_id || 'Custom'}</span>
                    {sc.created_by && <span className="text-[10px] font-body text-text-secondary">by {sc.created_by}</span>}
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-text-primary">{sc.title}</h4>
                  <p className="text-xs font-body text-text-secondary">{sc.description}</p>
                  {sc.projections?.delta && (
                    <div className="flex gap-4 mt-2 text-xs font-body">
                      <span className="text-emerald-400">+{sc.projections.delta.oee_points} OEE pp</span>
                      <span className="text-amber-400">{fmtVnd(sc.projections.delta.vnd_saved)} VND</span>
                      <span className="text-teal-400">-{sc.projections.delta.co2e_reduced_kg}kg CO2e</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {allCustom.length === 0 && !showBuilder && !customResult && (
            <div className="bg-surface border border-border rounded-lg p-8 text-center">
              <Robot size={32} className="text-text-secondary mx-auto mb-3" />
              <p className="text-sm font-body text-text-secondary">No custom scenarios yet.</p>
              <p className="text-xs font-body text-text-secondary mt-1">Click "Create Custom Scenario" to build your first AI-powered simulation.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
